import React, { useEffect, useRef, useState } from "react";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import leftArrow from "../../../../assets/images/dashboard/dashboard_chart/leftArrowImg.png";
import rightArrow from "../../../../assets/images/dashboard/dashboard_chart/rightArrowimg.png";

import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import {
  ConceptScoreData,
  ProfessionalAssessmentList,
} from "../../skill_score/interface/ScoreResponse_Interface";
import { max } from "date-fns";

// Register necessary Chart.js components
// ChartJS.register(
//   BarElement,
//   CategoryScale,
//   LinearScale,
//   Tooltip,
//   Legend,
//   ChartDataLabels
// );

type Question = {
  label: string;
  score: number;
};

type ChartDataProps = {
  data: {
    questions: Question[];
  };
};
export interface ChartComponentProps {
  data: ConceptScoreData[];
  barsToShows?: boolean;
}
const ChartComponent: React.FC<ChartComponentProps> = ({ data,barsToShows }) => {
  const questions = data.map((cs) => ({
    label: cs.concept_name,
    score: parseFloat(cs.concept_score.replace("%", "")),
  }));
const isSingleQuestion = questions.length === 1;

  const axisTooltipPlugin = {
    id: "xAxisTooltip",
    afterEvent: function (chart: any, args: any) {
      const { event } = args;

      // Get chart area and scales
      const xAxis = chart.scales.x;
      const chartArea = chart.chartArea;

      // Tooltip element setup
      let tooltipEl = document.getElementById("x-axis-tooltip");
      if (!tooltipEl) {
        tooltipEl = document.createElement("div");

        tooltipEl.id = "x-axis-tooltip";
        tooltipEl.style.position = "absolute";
        tooltipEl.style.background = "rgba(0, 0, 0, 0.8)";
        tooltipEl.style.color = "white";
        tooltipEl.style.padding = "6px 10px";
        tooltipEl.style.borderRadius = "6px";
        tooltipEl.style.fontSize = "12px";
        tooltipEl.style.whiteSpace = "nowrap";
        tooltipEl.style.pointerEvents = "none"; // prevents flickering
        tooltipEl.style.transition = "opacity 0.2s";
        tooltipEl.style.zIndex = "1000";
        document.body.appendChild(tooltipEl);
      }

      const x = event.x;
      const y = event.y;

      const labelIndex = xAxis.getValueForPixel(x);

      const index = Math.round(labelIndex);

      const label = chart.data.labels?.[index];
      const fullLabel = label || "";

      const maxLength = 10;
      const isTruncated = fullLabel.length > maxLength;

      if (
        isTruncated &&
        x > xAxis.left &&
        x < xAxis.right &&
        y > chartArea.bottom
      ) {
        tooltipEl.innerText = fullLabel;

        // Get label position in pixels
        const xPos = xAxis.getPixelForValue(index);
        const chartRect = chart.canvas.getBoundingClientRect();

        tooltipEl.style.opacity = "1";
        tooltipEl.style.left = `${
          chartRect.left + xPos - tooltipEl.offsetWidth / 2
        }px`;
        tooltipEl.style.top = `${chartRect.top + window.scrollY + chartArea.bottom - 5}px`;
      } else {
        tooltipEl.style.opacity = "0";
      }
    },
  };

  // Define colors for each bar (customize as needed)
  const colors = [
    "#ff4c4c", // red
    "#f7a021", // orange
    "#26b87e", // green
  ];
  const chartData = {
    labels: questions.map((q) => q.label),
  datasets: [
  {
    label: "Scores",
    data: questions.map((q) => q.score),
    backgroundColor: questions.map((q) => {
      if (q.score < 50) return "#ff4c4c";
      if (q.score < 75) return "#f7a021";
      return "#26b87e";
    }),
    borderRadius: 6,
        barThickness: questions.length === 1 ? 150 : undefined,
maxBarThickness: questions.length === 1 ? 200 : 100,
    barPercentage: questions.length===1 ? 0.6 : 0.7,
    categoryPercentage: questions.length===1 ? 0.6 : 0.8,
  },
],

  };

  const chartRef = useRef<any>(null);
  const barsToShow = barsToShows?8:4; // Show only 4 bars at a time
  const totalBars = chartData.labels.length;
  const [visibleRange, setVisibleRange] = useState<[number, number]>([
    0,
    barsToShow - 1,
  ]);
  // Disable scroll buttons based on position
  const isAtStart = visibleRange[0] === 0;
  const isAtEnd = visibleRange[1] >= totalBars - 1;
  // useEffect(() => {
  //   if (chartRef.current) {
  //     const scaleX = chartRef.current.scales.x;
  //     scaleX.options.min = visibleRange[0];
  //     scaleX.options.max = visibleRange[1];
  //     chartRef.current.update();
  //   }
  // }, [visibleRange]);
  
useEffect(() => {
  if (!chartRef.current) return;

  const scaleX = chartRef.current.scales.x;

  if (questions.length===1) {
    scaleX.options.min = -3.5;
    scaleX.options.max = 3.5;
  } else {
    scaleX.options.min = visibleRange[0];
    scaleX.options.max = visibleRange[1];
  }

  chartRef.current.update();
}, [visibleRange, isSingleQuestion]);
useEffect(() => {
  if (chartRef.current) {
    chartRef.current.resize();
    chartRef.current.update();
  }
}, [data]);

  const scrollLeft = () => {
    setVisibleRange(([min, max]) => {
      const newMin = Math.max(min - barsToShow, 0);
      const newMax = newMin + (barsToShow - 1);
      return [newMin, newMax];
    });
  };

  // Scroll Right
  const scrollRight = () => {
    if (visibleRange[1] < totalBars - 1) {
      setVisibleRange(([min, max]) => {
        const newMax = Math.min(max + barsToShow, totalBars - 1);
        const newMin = newMax - (barsToShow - 1);
        return [newMin, newMax];
      });
    }
  };
  const options: ChartOptions<"bar"> = {
    responsive: true,
  maintainAspectRatio: false,
    layout: {
      padding: {
        top: 20, // add spacing at top
        bottom: 10,
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        callbacks: {
          label: (context) => `${context.parsed.y}%`,
        },
      },
      // Display percentage labels on top of bars
      datalabels: {
        display: false,
        color: "#000",
        anchor: "end",
        align: "start",
        formatter: (value: number) => `${value}%`,
        font: {
          weight: "bold",
        },
      },
    },
    scales: {
      x: {
        min: visibleRange[0], // 👈 add this
        max: visibleRange[1],
        title: {
          display: true,
          text: "Concepts",
          color: "#414141",
          font: {
            family: "Mazzard",
            size: 16,
            weight: "bold",
          },
          padding: { top: 10, bottom: 0 },
        },
        ticks: {
          font: {
            family: "Mazzard",
            size: 16,
            weight: 500,
          },
          color: "#414141",
          autoSkip: false,
          maxRotation: 0,

          minRotation: 0,
          callback: function (value: any, index: number, values: any[]) {
            const label = chartData.labels[value];
            const maxLength = 10;
            if (label && label.length > maxLength) {
              return label.substring(0, maxLength) + "...";
            }
            return label;
          },
        },

        grid: { display: false },
        border: { display: false },
      },
      y: {
        max: 100,
        ticks: {
          stepSize: 20,
          callback: function (value) {
            return value === 0 ? "0%" : value + "%"; // 👈 Format 0 as 00%
          },
          font: {
            family: "Mazzard",
            size: 16,
            weight: 500,
          },

          color: "#414141",
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
          drawTicks: false,
        },
        border: {
          display: false,
          dash: [12, 6],
        },
        //  border: {  },
      },
    },
  };

  return (
    <div
      style={{
        // width: "100%",
        // maxWidth:questions.length===1?"100%": "700px",
        // width:questions.length===1?"900px":"700px",
        // width:questions.length===1?"1100px":"700px",
        // margin: "25px",
        //  padding: "25px 0",
        // height: "100%",
        backgroundColor: "transparent",
        borderRadius: "10px",
      }}
    >
      {/* <h1>{}</h1> */}
      {questions.length ? (
        <div style={{    flex: 1,
 display: "flex", alignItems: "center",  justifyContent: "center",
 }}>
          {/* Left Arrow Image */}
        {/* {!isSingleQuestion && ( */}
  <img
            src={leftArrow}
            alt="Left"
            onClick={isAtStart ? undefined : scrollLeft}
            style={{
              width: "45px",
              height: "45px",
              cursor: isAtStart ? "not-allowed" : "pointer",
              opacity: isAtStart ? 0.7 : 1,
            }}
          />
          {/* )} */}

          {/* Chart */}
          <div style={{  width: "100%",height:"300px",  }}>
            <Bar
              ref={chartRef}
              data={chartData}
              options={options}
              plugins={[axisTooltipPlugin]}
            />
          </div>

          {/* Right Arrow Image */}
        {/* {!isSingleQuestion && ( */}
  <img
            src={rightArrow}
            alt="Right"
            onClick={isAtEnd ? undefined : scrollRight}
            style={{
              width: "45px",
              height: "45px",
              cursor: isAtEnd ? "not-allowed" : "pointer",
              opacity: isAtEnd ? 0.7 : 1,
            }}
          />
          {/* )} */}
        </div>
      ) : (
        <p style={{ textAlign: "center", padding: "1rem" }}>
          No chart data available.
        </p>
      )}
    </div>
  );
};

export default ChartComponent;
